import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getBundleBySlug, getKitsForBundle, bundles, categories } from '../data/products';

const BundleDetailPage = () => {
  const { slug } = useParams();
  const { openCheckout } = useAuth();
  const bundle = getBundleBySlug(slug);

  if (!bundle) {
    return <Navigate to="/bundles" replace />;
  }

  const includedKits = getKitsForBundle(bundle.id);
  const pricePerKit = Math.round(bundle.price / bundle.kitCount);

  // Other bundles for comparison
  const otherBundles = bundles.filter(b => b.id !== bundle.id).slice(0, 3);

  return (
    <div className="bundle-detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="container">
          <Link to="/">Home</Link>
          <span className="separator">/</span>
          <Link to="/bundles">Bundles</Link>
          <span className="separator">/</span>
          <span className="current">{bundle.name}</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bundle-detail-hero">
        <div className="container">
          <div className="bundle-detail-grid">
            <div className="bundle-detail-info">
              {bundle.featured && (
                <span className="featured-tag">Best Value</span>
              )}
              <h1 className="bundle-detail-title">
                <span className="bundle-detail-icon">{bundle.icon}</span>
                {bundle.name}
              </h1>
              <p className="bundle-detail-description">{bundle.description}</p>

              <div className="bundle-detail-stats">
                <div className="detail-stat">
                  <span className="stat-value">{bundle.kitCount}</span>
                  <span className="stat-label">Interview Kits</span>
                </div>
                <div className="detail-stat">
                  <span className="stat-value">{bundle.questionCount}</span>
                  <span className="stat-label">Questions</span>
                </div>
                <div className="detail-stat highlight">
                  <span className="stat-value">₹{bundle.savings}</span>
                  <span className="stat-label">You Save</span>
                </div>
              </div>

              <div className="bundle-detail-highlights">
                <h3>Why this bundle?</h3>
                <ul className="highlights-list">
                  {bundle.highlights.map((highlight, i) => (
                    <li key={i}>
                      <span className="check-icon">✓</span>
                      {highlight}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bundle-detail-purchase">
              <div className="purchase-card">
                <div className="value-badge">
                  Save ₹{bundle.savings}
                </div>
                <div className="purchase-pricing">
                  <div className="original-price">
                    <span className="label">Total Value</span>
                    <span className="price">₹{bundle.originalPrice}</span>
                  </div>
                  <div className="bundle-price">
                    <span className="label">Bundle Price</span>
                    <div className="price-display">
                      <span className="currency">₹</span>
                      <span className="amount">{bundle.price}</span>
                    </div>
                  </div>
                  <div className="per-kit-price">
                    Only ₹{pricePerKit} per kit
                  </div>
                </div>

                <button
                  onClick={() => openCheckout({ id: bundle.id, name: bundle.name, price: bundle.price })}
                  className="btn btn-primary btn-large btn-full"
                >
                  Get Bundle - ₹{bundle.price}
                </button>


                <div className="purchase-includes">
                  <p className="includes-label">Instant access to:</p>
                  <ul>
                    <li>✓ All {bundle.kitCount} interview kits</li>
                    <li>✓ {bundle.questionCount} curated questions</li>
                    <li>✓ PDF format for all kits</li>
                    <li>✓ Free lifetime updates</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Included Kits Section */}
      <section className="included-kits-section">
        <div className="container">
          <h2 className="section-title">Included in this Bundle</h2>
          <p className="section-subtitle">
            {bundle.kitCount} premium interview kits worth ₹{bundle.originalPrice}
          </p>

          <div className="included-kits-grid">
            {includedKits.map(kit => (
              <div key={kit.id} className="included-kit-card">
                <div className="kit-header">
                  <span className="kit-icon">{kit.icon}</span>
                  <div className="kit-meta">
                    <h3>{kit.name}</h3>
                    <span className="kit-category">
                      {categories[kit.category]?.name}
                    </span>
                  </div>
                </div>
                <p className="kit-description">{kit.description}</p>
                <div className="kit-info">
                  <span className="kit-questions">{kit.questionCount} Questions</span>
                  <span className="kit-individual-price">
                    Individual: ₹{kit.price}
                  </span>
                </div>
                <div className="kit-topics">
                  {kit.topics.slice(0, 4).map((topic, i) => (
                    <span key={i} className="topic-tag">{topic}</span>
                  ))}
                  {kit.topics.length > 4 && (
                    <span className="topic-more">+{kit.topics.length - 4}</span>
                  )}
                </div>
                <Link to={`/kits/${kit.slug}`} className="kit-link">
                  View kit details →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Breakdown */}
      <section className="value-breakdown-section">
        <div className="container">
          <h2 className="section-title">Value Breakdown</h2>
          <div className="value-breakdown-card">
            <div className="breakdown-list">
              {includedKits.map(kit => (
                <div key={kit.id} className="breakdown-item">
                  <span className="item-name">
                    <span className="item-icon">{kit.icon}</span>
                    {kit.name}
                  </span>
                  <span className="item-price">₹{kit.price}</span>
                </div>
              ))}
              <div className="breakdown-divider"></div>
              <div className="breakdown-item total">
                <span className="item-name">Total Value</span>
                <span className="item-price">₹{bundle.originalPrice}</span>
              </div>
              <div className="breakdown-item bundle-price">
                <span className="item-name">Bundle Price</span>
                <span className="item-price highlight">₹{bundle.price}</span>
              </div>
              <div className="breakdown-item savings">
                <span className="item-name">You Save</span>
                <span className="item-price savings-amount">₹{bundle.savings}</span>
              </div>
            </div>
            <div className="breakdown-cta">
              <button
                onClick={() => openCheckout({ id: bundle.id, name: bundle.name, price: bundle.price })}
                className="btn btn-primary btn-large"
              >
                Get This Bundle
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Other Bundles */}
      {otherBundles.length > 0 && (
        <section className="other-bundles-section">
          <div className="container">
            <h2 className="section-title">Other Bundles</h2>
            <p className="section-subtitle">Explore more bundle options</p>
            <div className="other-bundles-grid">
              {otherBundles.map(otherBundle => (
                <div key={otherBundle.id} className="other-bundle-card">
                  <div className="other-bundle-header">
                    <span className="other-bundle-icon">{otherBundle.icon}</span>
                    <h3>{otherBundle.name}</h3>
                  </div>
                  <p className="other-bundle-stats">
                    {otherBundle.kitCount} kits • {otherBundle.questionCount} questions
                  </p>
                  <div className="other-bundle-footer">
                    <span className="other-bundle-price">₹{otherBundle.price}</span>
                    <Link to={`/bundles/${otherBundle.slug}`} className="btn btn-outline btn-small">
                      View
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <div className="view-all-bundles">
              <Link to="/bundles" className="btn btn-outline">
                View All Bundles
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="bundle-final-cta">
        <div className="container">
          <div className="final-cta-content">
            <h2>Get interview-ready with {bundle.name}</h2>
            <p>{bundle.kitCount} kits • {bundle.questionCount} questions • ₹{bundle.savings} savings</p>
            <button
              onClick={() => openCheckout({ id: bundle.id, name: bundle.name, price: bundle.price })}
              className="btn btn-primary btn-large"
            >
              Get Bundle - ₹{bundle.price}
            </button>
            <p className="guarantee-text">Instant download • PDF delivered to your email</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BundleDetailPage;
